import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import * as authApi from '@/features/auth/api';
import { AUTH_SESSIONS_QUERY_KEY } from '@/features/auth/hooks';
import type { CurrentUser, LoginInput, RegisterInput } from '@/features/auth/types';
import {
  clearAccessToken,
  getAccessToken,
  onUnauthorized,
  setAccessToken,
} from '@/lib/api/auth-token';
import { clearSessionFamilyId, setSessionFamilyId } from '@/lib/api/session-family';

type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  readonly status: SessionStatus;
  readonly user: CurrentUser | null;
  readonly login: (input: LoginInput) => Promise<void>;
  readonly register: (input: RegisterInput) => Promise<void>;
  readonly logout: () => Promise<void>;
  /**
   * Re-resolves the current user via `/auth/me` without touching the
   * access token or `status` (ADR-026 Addendum, AO-13) - used after a
   * Guardian invitation is accepted, since that call provisions a new
   * organization membership the frontend must not guess at from the
   * acceptance response alone.
   */
  readonly refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * The one centralized auth/session layer (ADR-026, Decision F3). Owns:
 * restoring the access token from `sessionStorage` on boot, setting it
 * after login, clearing it on logout, and reacting to a 401 observed
 * anywhere in the app (via `onUnauthorized`). The refresh token login
 * returns is held only in a ref - never persisted - so it survives for
 * the current page load only (features/auth/api.ts's `login` doc comment
 * explains why).
 */
export function AuthProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUser] = useState<CurrentUser | null>(null);
  const refreshTokenRef = useRef<string | null>(null);
  const queryClient = useQueryClient();

  /**
   * Removes account-level cached data that outlives a single tenant
   * context and so isn't touched by `clearTenantQueries()`
   * (active-organization-context.tsx) - currently just the session/device
   * list (features/auth/hooks.ts). Without this, a different account
   * logging into the same tab within the query's staleTime
   * (app/query-client.ts) could see the previous account's cached list.
   */
  const clearAccountLevelQueries = useCallback((): void => {
    queryClient.removeQueries({ queryKey: AUTH_SESSIONS_QUERY_KEY });
  }, [queryClient]);

  useEffect(() => {
    let cancelled = false;

    async function restore(): Promise<void> {
      const token = getAccessToken();
      if (token === null) {
        if (!cancelled) {
          setStatus('unauthenticated');
        }
        return;
      }

      try {
        const restoredUser = await authApi.getCurrentUser();
        if (!cancelled) {
          setUser(restoredUser);
          setStatus('authenticated');
        }
      } catch {
        // Token present but no longer valid (expired/revoked) - fail closed.
        clearAccessToken();
        if (!cancelled) {
          setStatus('unauthenticated');
        }
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    () =>
      onUnauthorized(() => {
        refreshTokenRef.current = null;
        clearSessionFamilyId();
        clearAccountLevelQueries();
        setUser(null);
        setStatus('unauthenticated');
      }),
    [clearAccountLevelQueries],
  );

  const login = useCallback(async (input: LoginInput): Promise<void> => {
    const result = await authApi.login(input);
    setAccessToken(result.accessToken);
    setSessionFamilyId(result.sessionFamilyId);
    refreshTokenRef.current = result.refreshToken;
    setUser(result.user);
    setStatus('authenticated');
  }, []);

  const register = useCallback(async (input: RegisterInput): Promise<void> => {
    await authApi.register(input);
  }, []);

  const refreshUser = useCallback(async (): Promise<void> => {
    const restoredUser = await authApi.getCurrentUser();
    setUser(restoredUser);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    const refreshToken = refreshTokenRef.current;
    if (refreshToken !== null) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        // Best-effort server-side invalidation - local state is cleared
        // unconditionally below regardless of whether this call succeeds.
      }
    }
    refreshTokenRef.current = null;
    clearAccessToken();
    clearSessionFamilyId();
    clearAccountLevelQueries();
    setUser(null);
    setStatus('unauthenticated');
  }, [clearAccountLevelQueries]);

  const value: AuthContextValue = { status, user, login, register, logout, refreshUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }
  return value;
}
